import styled from "@emotion/styled";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { aprToApy } from "@stepfinance/common";
import type {
  FarmActions,
  FarmPlatforms,
  FarmPositions,
  RaydiumFarmPosition,
  StepFarmPosition,
} from "@stepfinance/farms";
import { FarmType, isRaydiumV4V5Farm, pendingRewardReducer } from "@stepfinance/farms";
import { isFeesAprFarm } from "@stepfinance/farms/src/lib/positions";
import type { LiquidityPoolPlatforms } from "@stepfinance/liquidity";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  ExtraBoldText,
  formatBalance,
  getColumnSearchProps,
  imageFromPlatformName,
  makeSimpleFilters,
  StepAssetBreakdownPopoverChip,
  StepButtonDropdown,
  StepPopoverCellWrapper,
  StepTable,
  transparentWhite,
  URLIcon,
} from "@stepfinance/react";
import { Divider } from "antd";
import type { TablePaginationConfig } from "antd/lib/table";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { deposit, depositV4, depositV5 } from "../../../actions/raydium";
import { claimRewards } from "../../../actions/step/reward-pool";
import { ReactComponent as YieldFarmingIcon } from "../../../assets/yield-farming.svg";
import { useAccountsContext } from "../../../contexts/accounts";
import { DUST_THRESHOLD, useHideDustConfig } from "../../../contexts/app/config";
import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useFarms } from "../../../contexts/farm";
import { useLanguageContext } from "../../../contexts/language/language";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { useWithdrawAndCloseStepFarm } from "../../../hooks/mutations/farms/useWithdrawAndCloseStepFarm";
import { useConnectedWallet } from "../../../hooks/wallet/useConnectedWallet";
import { handlePreSendTxnError } from "../../../utils/transactions";
import { capitalizeText, isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import {
  APRBreakdownInfo,
  IconTextContainer,
  PAGE_SIZE_OPTIONS,
  StepPopoverAssetWithIconRow,
  TokenIconText,
} from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import type { PoolCellProps } from "./PoolCell";
import { PoolCell } from "./PoolCell";

const isStepCompoundableFarm = (farm: FarmPositions): farm is StepFarmPosition => {
  return (
    farm.kind === "Step Finance" &&
    farm.liquidityPool.mints.filter((m) => m === farm.rewardPool.account.rewardAMint.toBase58()).length > 0 &&
    farm.type !== FarmType.DUAL_REWARD &&
    farm.status === "active"
  );
};

export const FarmGrid: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { hideDust } = useHideDustConfig();
  const wallet = useConnectedWallet();
  const { watching } = useEnrichedWallet();

  const { connection } = useConnection();
  const { userTokenAccounts } = useAccountsContext();
  const { farmPositions, refreshFarms, setFarmBalance } = useFarms();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [claimingId, setClaimingId] = useState<string>("");
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "farmGridPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      components: {
        table: { assetBreakdownPopover, rewardBreakdown },
      },
      pages: {
        dashboard: {
          modules: { farmGrid: farmGridText },
        },
      },
    },
  } = useLanguageContext();

  const withdrawAndCloseStepFarmMutation = useWithdrawAndCloseStepFarm();

  const farmActionsMap: { [Platform in FarmPlatforms]: FarmActions<FarmPositions> } = {
    Aldrin: {},
    Almond: {},
    Atrix: {},
    Marinade: {},
    Mercurial: {},
    Orca: {},
    Quarry: {},
    Raydium: {
      onClaim: (farm) => {
        if (farm.kind === "Raydium") {
          void handleRaydiumClaim(farm);
        }
      },
    },
    "Step Finance": {
      onClaim: (farm) => {
        if (farm.kind === "Step Finance") {
          void handleStepClaim(farm);
        }
      },
      onUnstake: (farm) => {
        if (farm.kind === "Step Finance") {
          void withdrawAndCloseStepFarmMutation.mutate(farm);
        }
      },
    },
  };
  const filteredPools = useMemo(() => {
    const allFarms = farmPositions;
    if (hideDust) {
      return allFarms.filter((pool) => {
        const pendingRewardUSD = pool.rewards.reduce(pendingRewardReducer, 0);
        const poolValue = (pool.valueInUSD ?? 0) + pendingRewardUSD;
        return (poolValue || 0) > DUST_THRESHOLD;
      });
    }
    return allFarms;
  }, [farmPositions, hideDust]);

  const totalFarmValue = useMemo(() => {
    return filteredPools.reduce(function (total, currentValue) {
      const pendingRewardUSD = currentValue.rewards.reduce(pendingRewardReducer, 0);
      return total + ((currentValue.valueInUSD ?? 0) + pendingRewardUSD);
    }, 0);
  }, [filteredPools]);

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(filteredPools.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSize: parseInt(paginationSetting),
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
    });
  }, [filteredPools.length, paginationSetting]);

  useEffect(() => {
    setFarmBalance(totalFarmValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalFarmValue]);

  const handleStepClaim = (farmInfo: StepFarmPosition) => {
    const { rewardPool, userStake, rewards, type } = farmInfo;
    const isDualReward = type === FarmType.DUAL_REWARD;
    const { rewardAMint, rewardBMint } = rewardPool.account;
    if (wallet && userStake) {
      claimRewards(
        connection,
        wallet,
        rewardPool.publicKey,
        userStake.publicKey,
        rewards[0].rewardBalance,
        rewardAMint,
        isDualReward ? rewards[1].rewardBalance : undefined,
        isDualReward ? rewardBMint : undefined,
      )
        .catch((err) => {
          captureException(err);
          handlePreSendTxnError(err as Error, "Error claiming rewards");
        })
        .finally(() => {
          void refreshFarms();
        });
    }
  };

  const handleRaydiumClaim = async (farmInfo: RaydiumFarmPosition) => {
    const infoAccount = farmInfo.stakeAccountAddress;

    let rewardAccount: PublicKey | undefined;
    const lpAccount: string | undefined =
      farmInfo.version === 2
        ? userTokenAccounts.find((x) => x.info.mint === farmInfo.rewards[0].rewardToken.address)?.pubkey
        : userTokenAccounts.find((x) => x.info.mint === farmInfo.lpMintAddress)?.pubkey;
    try {
      setClaimingId(farmInfo.poolId);
      if (isRaydiumV4V5Farm(farmInfo)) {
        const deposit = farmInfo.version === 5 ? depositV5 : depositV4;
        await deposit(connection, wallet, farmInfo, new PublicKey(lpAccount!), infoAccount, "0");
      } else {
        await deposit(connection, wallet, farmInfo, new PublicKey(lpAccount!), rewardAccount, infoAccount, "0");
      }
    } catch (error: unknown) {
      handlePreSendTxnError(error as Error, "Error claiming rewards");
    } finally {
      setClaimingId("");
      await refreshFarms();
    }
  };

  const columns: StepColumnsType<FarmPositions> = [
    {
      title: common.platform,
      dataIndex: "kind",
      key: "kind",
      filters: makeSimpleFilters(filteredPools.map((pool) => pool.kind)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.kind.startsWith(value as LiquidityPoolPlatforms),
      filteredValue: filteredInfo.kind ?? null,
      render(_, { kind }) {
        const image = imageFromPlatformName(kind);
        return (
          <IconTextContainer>
            <URLIcon src={image ?? ""} />
            <TokenIconText bold>
              <CellValue cellType="shortenedText">{kind}</CellValue>
            </TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => a.kind.localeCompare(b.kind),
    },
    {
      title: common.pool,
      dataIndex: "lpName",
      key: "lpName",
      render(_, record) {
        let newRecord: PoolCellProps = record;
        const analyticsLink = record.key && `https://analytics.step.finance/defionsolana/farm?farmId=${record.key}`;
        if (analyticsLink) {
          newRecord = {
            ...record,
            analyticsLink,
          };
        }
        return <PoolCell {...newRecord} />;
      },
      ...getColumnSearchProps("lpName", "pool"),
      filteredValue: filteredInfo.lpName ?? null,
      sorter: ({ lpName: poolA }, { lpName: poolB }) => poolA.localeCompare(poolB),
    },
    {
      title: "APR",
      dataIndex: "apr",
      key: "apr",
      className: "column-number",
      render(_, record) {
        let feesApr: number | null = null;
        if (isFeesAprFarm(record)) {
          feesApr = record.feesApr;
        }
        let totalApr: number | null = null;
        if (record.apr !== null) {
          totalApr = (totalApr ?? 0) + record.apr;
        }
        if (feesApr !== null) {
          totalApr = (totalApr ?? 0) + feesApr * 100;
        }
        const apy = totalApr !== null ? aprToApy(totalApr) / 100 : null;
        totalApr = totalApr !== null ? totalApr / 100 : null;
        const APRBreakdownContent = (
          <APRBreakdownInfo width={248}>
            {feesApr !== null && (
              <div>
                <ExtraBoldText>{farmGridText.aprBreakdown.tradingFees}</ExtraBoldText>
                <CellValue cellType="percentage">{feesApr}</CellValue>
              </div>
            )}
            <div>
              <ExtraBoldText>{capitalizeText(common.rewards)}</ExtraBoldText>
              <CellValue cellType="percentage">{record.apr !== null ? record.apr / 100 : null}</CellValue>
            </div>
            <div>
              <ExtraBoldText>{farmGridText.aprBreakdown.projectedReturns}</ExtraBoldText>
            </div>
            <Divider />
            <div>
              <div>
                <ExtraBoldText>APY</ExtraBoldText>
                <CoumpoundInfoLabel>{farmGridText.aprBreakdown.compoundedDaily}</CoumpoundInfoLabel>
              </div>
              <CellValue cellType="percentage">{apy}</CellValue>
            </div>
          </APRBreakdownInfo>
        );
        return (
          <APRCellContainer>
            <APRPercantageContainer>
              <CellValue cellType="percentage">{totalApr}</CellValue>
            </APRPercantageContainer>
            <StepPopoverCellWrapper>
              <StepAssetBreakdownPopoverChip
                title={farmGridText.aprBreakdown.title}
                content={APRBreakdownContent}
                onlyContent={true}
              />
            </StepPopoverCellWrapper>
          </APRCellContainer>
        );
      },
      sorter: (a, b) => {
        const aFeesApr = isFeesAprFarm(a) && a.feesApr ? a.feesApr * 100 : 0;
        const bFeesApr = isFeesAprFarm(b) && b.feesApr ? b.feesApr * 100 : 0;
        return (a.apr ?? 0) + aFeesApr - ((b.apr ?? 0) + bFeesApr);
      },
    },
    {
      title: common.pendingRewards,
      dataIndex: "pending",
      key: "pending",
      className: "column-number",
      render(_, record) {
        const pendingRewardUSD = record.rewards.reduce(pendingRewardReducer, 0);
        const compoundButtonDisabled = !isStepCompoundableFarm(record) || pendingRewardUSD < 0.5;
        const rewardRows = record.rewards
          .filter((r) => r.rewardBalance > 0)
          .map((r) => {
            const rewardBalanceDisplay: string = formatBalance(r.rewardBalance, r.rewardToken?.decimals);
            const symbol: string = r.rewardToken?.symbol;

            return (
              <StepPopoverAssetWithIconRow
                key={`${record.kind}-${r.rewardToken.address}`}
                tokenIconMintAddress={r.rewardToken?.address}
                amount={rewardBalanceDisplay}
                symbol={symbol}
              />
            );
          }, []);
        const showMultipleButton =
          (!!farmActionsMap[record.kind].onClaim || !!farmActionsMap[record.kind].onCompound) && !watching;
        return (
          <PendingRewardsContainer spaceBetween={showMultipleButton}>
            {showMultipleButton && (
              <StepButtonDropdown
                onButtonClick={() => farmActionsMap[record.kind].onClaim?.(record)}
                buttonTitle={common.claim}
                items={[
                  {
                    label: common.claim,
                    hidden: record.rewards.reduce((a, b) => a + b.rewardBalance, 0) <= 0,
                    loading: claimingId === record.lpName,
                    onClick: () => farmActionsMap[record.kind].onClaim?.(record),
                  },
                  {
                    label: common.compoundButton,
                    hidden: compoundButtonDisabled,
                    onClick: () => farmActionsMap[record.kind].onCompound?.(record),
                  },
                  {
                    label: common.unstake,
                    hidden: record.kind !== "Step Finance",
                    onClick: () => farmActionsMap[record.kind].onUnstake?.(record),
                  },
                ]}
              />
            )}
            <PopoverAssetContainer>
              {pendingRewardUSD < 0.01 ? (
                <PendingLabel>{common.pending}</PendingLabel>
              ) : (
                <StepPopoverCellWrapper>
                  <StepAssetBreakdownPopoverChip
                    title={rewardBreakdown.title}
                    content={rewardRows}
                    assetValue={pendingRewardUSD}
                    assetValueDisplay={<CellValue cellType="userPositionValue">{pendingRewardUSD}</CellValue>}
                  />
                </StepPopoverCellWrapper>
              )}
            </PopoverAssetContainer>
          </PendingRewardsContainer>
        );
      },
      sorter: (a, b) => a.rewards.reduce(pendingRewardReducer, 0) - b.rewards.reduce(pendingRewardReducer, 0),
    },
    {
      title: common.value,
      dataIndex: "value",
      key: "value",
      className: "column-number",
      render(_, record) {
        const liquidityComponents = record.liquidity.map((liquidityHalf) => {
          const amount = formatBalance(liquidityHalf.amount ?? 0, liquidityHalf.token?.decimals);
          const symbol = liquidityHalf.token?.symbol || "";
          const mintAddress = liquidityHalf.token?.address;

          return (
            <StepPopoverAssetWithIconRow
              key={`${record.kind}-${liquidityHalf.token?.address}`}
              tokenIconMintAddress={mintAddress}
              amount={amount}
              symbol={symbol}
            />
          );
        });
        const positionValue: number = record.valueInUSD ?? 0;

        return (
          <StepPopoverCellWrapper>
            <StepAssetBreakdownPopoverChip
              title={assetBreakdownPopover.title}
              content={liquidityComponents}
              assetValue={positionValue}
              assetValueDisplay={<CellValue cellType="userPositionValue">{positionValue}</CellValue>}
            />
          </StepPopoverCellWrapper>
        );
      },

      sorter: (a, b) => (a.valueInUSD ?? 0) - (b.valueInUSD ?? 0),
      defaultSortOrder: "descend" as any,
    },
  ];
  return (
    <>
      {filteredPools.length > 0 && (
        <FarmGridContainer>
          <ChevronCollapse
            title={common.yieldFarming}
            value={totalFarmValue}
            TitleIcon={YieldFarmingIcon}
            activePanels={activePanels}
            panelKey={getPanelKey("FarmGrid")}
            onChange={onPanelClick}
          >
            <StepTable
              columns={columns}
              dataSource={filteredPools}
              pagination={pagination}
              onChange={(pagination, filters) => {
                setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
                setFilteredInfo(filters);
              }}
            />
          </ChevronCollapse>
        </FarmGridContainer>
      )}
    </>
  );
};

const APRCellContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FarmGridContainer = styled.div`
  margin-top: 20px;
`;

const APRPercantageContainer = styled.div`
  margin-right: 10px;
`;

const PopoverAssetContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 170px;
  margin-left: 10px;
`;

const PendingRewardsContainer = styled.div<{ spaceBetween: boolean }>`
  display: flex;
  width: 100%;
  justify-content: ${(props) => (props.spaceBetween ? "space-between" : "flex-end")};
`;

const CoumpoundInfoLabel = styled.span`
  color: ${transparentWhite};
`;

const PendingLabel = styled.span`
  color: ${transparentWhite};
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
`;
